import React, { useMemo } from 'react'
import { Box, LinearProgress, SxProps, Theme, styled } from '@mui/material'
import { INTERAX_COLORS, TYPOGRAPHY } from '../constants/interaxColors'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'

const StyledProgressBar = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'progressValue' && prop !== 'isCompleted'
})<{ progressValue: number; isCompleted: boolean }>(({ progressValue, isCompleted }) => ({
  position: 'relative',
  width: '100%',
  height: progressValue === 0 ? '11px' : '11.8px',
  borderRadius: progressValue === 0 ? '16px' : '10px',
  backgroundColor:
    progressValue === 0 ? INTERAX_COLORS.heritageBlue : isCompleted ? INTERAX_COLORS.greenBackground : 'transparent',
  border:
    progressValue === 0
      ? 'none'
      : isCompleted
        ? `1px solid ${INTERAX_COLORS.green}`
        : '0.24px solid rgba(255, 255, 255, 0.25)',
  boxShadow:
    progressValue === 0
      ? 'none'
      : isCompleted
        ? `0px 0px 4px ${INTERAX_COLORS.greenBackground}`
        : '0px 0px 2.4px rgba(0, 0, 0, 0.5), 0px 0px 4.8px rgba(255, 171, 64, 0.4)',
  '& .MuiLinearProgress-bar': {
    borderRadius: '10px',
    background: isCompleted
      ? INTERAX_COLORS.greenBackground
      : 'linear-gradient(360deg, #FFD196 0%, #FFAB40 23.08%, #FFFFFF 100%)',
    transition: 'transform 0.3s ease-in-out'
  }
}))

const StatusPill = styled(Box)(() => ({
  ...TYPOGRAPHY.buttonSmall,
  fontSize: '10px',
  color: INTERAX_COLORS.white,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '4px 8px',
  borderRadius: '4px',
  fontWeight: 600,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  alignSelf: 'flex-start'
}))

export interface SprintActivityProgressStatusProps {
  isCompleted: boolean
  isInProgress: boolean
  className?: string
  sx?: SxProps<Theme>
}

export const SprintActivityProgressStatus: React.FC<SprintActivityProgressStatusProps> = ({
  isCompleted,
  isInProgress,
  className,
  sx
}) => {
  const { t } = useTranslation()

  const progressValue = useMemo(() => {
    if (isCompleted) {
      return 100
    }

    if (isInProgress) {
      return 100
    }

    return 0
  }, [isCompleted, isInProgress])

  const statusConfig = useMemo(() => {
    if (isCompleted) {
      return {
        label: t('gamer.sprintProgress.completed', 'Completed'),
        backgroundColor: INTERAX_COLORS.green
      }
    }

    if (isInProgress) {
      return {
        label: t('gamer.sprintProgress.inProgress', 'In progress'),
        backgroundColor: INTERAX_COLORS.sun
      }
    }

    return {
      label: null,
      backgroundColor: 'transparent'
    }
  }, [isCompleted, isInProgress, t])

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        ...sx
      }}
    >
      <StyledProgressBar
        progressValue={progressValue}
        isCompleted={isCompleted}
        variant="determinate"
        value={progressValue}
      />
      {statusConfig.label ? (
        <StatusPill sx={{ backgroundColor: statusConfig.backgroundColor }}>{statusConfig.label}</StatusPill>
      ) : null}
    </Box>
  )
}

export default SprintActivityProgressStatus
